import { makeStyles } from '@material-ui/styles'
import ExternalLink from 'core/components/ExternalLink'
import Badge from 'core/elements/badge'
import Theme from 'core/themes/model'
import React, { useState } from 'react'
import TroubleShootModal from './TroubleShootModal'
import {
  IMAGE_SERVICE_HELP_LINK,
  NETWORKING_SERVICE_HELP_LINK,
  PERSISTENT_STORAGE_SERVICE_HELP_LINK,
  COMPUTE_SERVICE_HELP_LINK,
} from './constant'
import { getHealthStatus } from './helper'
import { ServiceType } from './constant'
import { ServiceStatus } from './selector'

export const AcceptCertificate = ({
  endpoint,
  message = 'Make sure you have accepted the certificate for this service endpoint.',
}: {
  endpoint: string
  message?: string
}) => {
  return (
    <>
      {message}{' '}
      {endpoint && (
        <ExternalLink textVariant="caption1" url={endpoint}>
          Click to Accept
        </ExternalLink>
      )}
    </>
  )
}

export interface TroubleshootOptions {
  serviceType: ServiceType
  label: string
}

interface HealthStatusBadgeProps {
  value: any
  serviceType: ServiceType
  endpoint?: string
  combinedServiceState?: boolean
}

const useColumnStyles = makeStyles((theme: Theme) => ({
  serviceHealthBadge: {
    cursor: 'pointer',
  },
}))

export const HealthStatusBadge: React.FC<HealthStatusBadgeProps> = ({
  value,
  serviceType,
  endpoint,
  combinedServiceState,
}) => {
  const classes = useColumnStyles()
  const { label, variant } = getHealthStatus(value)
  const [troubleshootOptions, setTroubleshootOptions] = useState<TroubleshootOptions | null>(null)
  const isError = variant !== 'success' && label !== 'Not Configured'

  const serviceName = {
    [ServiceType.Compute]: 'Compute Service',
    [ServiceType.Image]: 'Image Library Service',
    [ServiceType.Storage]: 'Persistent Storage Service',
    [ServiceType.Network]: 'Networking Service',
  }[serviceType]

  return (
    <>
      <Badge
        className={isError ? classes.serviceHealthBadge : ''}
        variant={variant}
        text={label}
        onClick={(e) => {
          if (isError) {
            setTroubleshootOptions({ serviceType, label })
            e.stopPropagation()
            e.preventDefault()
          }
        }}
        ellipsisAt={50}
      />
      {isError && (
        <TroubleShootModal
          open={!!troubleshootOptions}
          onClose={() => setTroubleshootOptions(null)}
          serviceName={serviceName}
          linkUrl={
            serviceType === ServiceType.Image
              ? IMAGE_SERVICE_HELP_LINK
              : serviceType === ServiceType.Storage
              ? PERSISTENT_STORAGE_SERVICE_HELP_LINK
              : serviceType === ServiceType.Network
              ? NETWORKING_SERVICE_HELP_LINK
              : serviceType === ServiceType.Compute
              ? COMPUTE_SERVICE_HELP_LINK
              : ''
          }
          title="Troubleshooting Steps"
          combinedServiceState={combinedServiceState}
          maxWidth={serviceType === ServiceType.Image ? 700 : 600}
          steps={
            label === ServiceStatus.CertificateError
              ? [
                  {
                    id: 1,
                    text: (
                      <AcceptCertificate
                        endpoint={endpoint}
                        message={`Make sure you have accepted the image library certificate for ${
                          endpoint ? 'this host.' : 'all the image service hosts.'
                        }`}
                      />
                    ),
                  },
                ]
              : label === ServiceStatus.NetworkError
              ? [
                  {
                    id: 1,
                    text:
                      'Make sure that the image library service is accessible from the network you are on.',
                  },
                  {
                    id: 2,
                    text: (
                      <AcceptCertificate
                        endpoint={endpoint}
                        message={`Make sure you have accepted the image library certificate for ${
                          endpoint ? 'this host.' : 'all the image service hosts.'
                        }`}
                      />
                    ),
                  },
                  {
                    id: 3,
                    text: (
                      <>
                        Read{' '}
                        <ExternalLink textVariant="caption1" url={IMAGE_SERVICE_HELP_LINK}>
                          here
                        </ExternalLink>{' '}
                        for more info on how to debug this
                      </>
                    ),
                  },
                ]
              : []
          }
        />
      )}
    </>
  )
}
